/**
 * useItemMaster Hook
 * Comprehensive hook for managing inventory items with full CRUD operations
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import {
  InventoryItem,
  InventoryItemWithDetails,
  InventoryItemFormData,
  ItemFilters,
  ItemSortOptions,
  ItemMasterStats,
  ItemImportData,
  ItemExportData,
  UseItemMasterReturn,
  ItemType,
} from '@/types/itemMaster';

export function useItemMaster(): UseItemMasterReturn {
  const { toast } = useToast();
  const [items, setItems] = useState<InventoryItemWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ItemMasterStats | null>(null);

  /**
   * Fetch items with optional filters and sorting
   */
  const fetchItems = useCallback(async (
    filters?: ItemFilters,
    sort?: ItemSortOptions
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('item_master_view')
        .select('*');

      // Apply filters
      if (filters?.search) {
        query = query.or(`code.ilike.%${filters.search}%,name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,barcode.ilike.%${filters.search}%`);
      }

      if (filters?.category_id) {
        query = query.eq('category_id', filters.category_id);
      }

      if (filters?.item_type) {
        query = query.eq('item_type', filters.item_type);
      }

      if (filters?.make_or_buy) {
        query = query.eq('make_or_buy', filters.make_or_buy);
      }

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.stock_status) {
        query = query.eq('stock_status', filters.stock_status);
      }

      if (filters?.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }

      if (filters?.warehouse_id) {
        query = query.eq('default_warehouse_id', filters.warehouse_id);
      }

      if (filters?.supplier_id) {
        query = query.eq('supplier_id', filters.supplier_id);
      }

      // Apply sorting
      const sortField = sort?.field || 'created_at';
      const sortDirection = sort?.direction === 'asc' ? true : false;
      query = query.order(sortField, { ascending: sortDirection });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setItems(data || []);

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch items';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Get a single item by ID
   */
  const getItem = useCallback(async (id: string): Promise<InventoryItemWithDetails | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('item_master_view')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      return data;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch item';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  /**
   * Generate a unique item code
   */
  const generateItemCode = useCallback(async (categoryCode?: string): Promise<string> => {
    try {
      const prefix = categoryCode || 'ITM';
      
      const { data, error: rpcError } = await supabase
        .rpc('generate_item_code', { p_category_code: prefix });

      if (rpcError) throw rpcError;
      return data;

    } catch (err) {
      // Fallback: generate code client-side
      const timestamp = Date.now().toString(36).toUpperCase();
      return `${categoryCode || 'ITM'}-${timestamp}`;
    }
  }, []);

  /**
   * Create a new item
   */
  const createItem = useCallback(async (formData: InventoryItemFormData): Promise<InventoryItem | null> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Generate code if not provided
      let itemCode = formData.code;
      if (!itemCode) {
        // Get category code for prefix
        let categoryCode = 'ITM';
        if (formData.category_id) {
          const { data: category } = await supabase
            .from('item_categories')
            .select('code')
            .eq('id', formData.category_id)
            .single();
          if (category) {
            categoryCode = category.code;
          }
        }
        itemCode = await generateItemCode(categoryCode);
      }

      const insertData = {
        code: itemCode,
        name: formData.name,
        description: formData.description || null,
        category_id: formData.category_id || null,
        category: 'Uncategorized', // Legacy field - always set a value since column is NOT NULL
        item_type: formData.item_type,
        make_or_buy: formData.make_or_buy,
        unit: formData.unit,
        uom_id: formData.uom_id || null,
        stock_level: formData.stock_level ?? 0,
        min_stock_level: formData.min_stock_level ?? 0,
        reorder_level: formData.reorder_level ?? 0,
        max_stock_level: formData.max_stock_level || null,
        lead_time_days: formData.lead_time_days ?? 7,
        unit_cost: formData.unit_cost ?? 0,
        default_warehouse_id: formData.default_warehouse_id || null,
        supplier_id: formData.supplier_id || null,
        barcode: formData.barcode || null,
        specifications: formData.specifications || {},
        status: formData.status ?? 'active',
        is_active: formData.is_active ?? true,
        created_by: user?.id,
      };

      const { data, error: insertError } = await supabase
        .from('inventory_items')
        .insert(insertData)
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: 'Success',
        description: `Item "${formData.name}" (${itemCode}) created successfully`,
      });

      // Refresh items list
      await fetchItems();
      return data;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create item';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchItems, generateItemCode, toast]);

  /**
   * Update an existing item
   */
  const updateItem = useCallback(async (
    id: string,
    formData: Partial<InventoryItemFormData>
  ): Promise<InventoryItem | null> => {
    try {
      setLoading(true);
      setError(null);

      const updateData: Record<string, unknown> = {};
      
      if (formData.code !== undefined) updateData.code = formData.code;
      if (formData.name !== undefined) updateData.name = formData.name;
      if (formData.description !== undefined) updateData.description = formData.description;
      if (formData.category_id !== undefined) updateData.category_id = formData.category_id;
      if (formData.item_type !== undefined) updateData.item_type = formData.item_type;
      if (formData.make_or_buy !== undefined) updateData.make_or_buy = formData.make_or_buy;
      if (formData.unit !== undefined) updateData.unit = formData.unit;
      if (formData.uom_id !== undefined) updateData.uom_id = formData.uom_id;
      if (formData.stock_level !== undefined) updateData.stock_level = formData.stock_level;
      if (formData.min_stock_level !== undefined) updateData.min_stock_level = formData.min_stock_level;
      if (formData.reorder_level !== undefined) updateData.reorder_level = formData.reorder_level;
      if (formData.max_stock_level !== undefined) updateData.max_stock_level = formData.max_stock_level;
      if (formData.lead_time_days !== undefined) updateData.lead_time_days = formData.lead_time_days;
      if (formData.unit_cost !== undefined) updateData.unit_cost = formData.unit_cost;
      if (formData.default_warehouse_id !== undefined) updateData.default_warehouse_id = formData.default_warehouse_id;
      if (formData.supplier_id !== undefined) updateData.supplier_id = formData.supplier_id;
      if (formData.barcode !== undefined) updateData.barcode = formData.barcode;
      if (formData.specifications !== undefined) updateData.specifications = formData.specifications;
      if (formData.status !== undefined) updateData.status = formData.status;
      if (formData.is_active !== undefined) updateData.is_active = formData.is_active;

      const { data, error: updateError } = await supabase
        .from('inventory_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Item updated successfully',
      });

      // Refresh items list
      await fetchItems();
      return data;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update item';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchItems, toast]);

  /**
   * Delete an item
   */
  const deleteItem = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if item is used in work orders
      const { data: workOrderMaterials } = await supabase
        .from('work_order_materials')
        .select('id')
        .eq('material_id', id)
        .limit(1);

      if (workOrderMaterials && workOrderMaterials.length > 0) {
        throw new Error('Cannot delete item that is used in work orders');
      }

      // Check if item has inventory transactions
      const { data: transactions } = await supabase
        .from('inventory_tx')
        .select('id')
        .eq('item_id', id)
        .limit(1);

      if (transactions && transactions.length > 0) {
        throw new Error('Cannot delete item with inventory transaction history. Consider deactivating instead.');
      }

      const { error: deleteError } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Item deleted successfully',
      });

      // Refresh items list
      await fetchItems();
      return true;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete item';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchItems, toast]);

  /**
   * Fetch item statistics
   */
  const fetchStats = useCallback(async (): Promise<ItemMasterStats | null> => {
    try {
      const { data: allItems, error: fetchError } = await supabase
        .from('item_master_view')
        .select('id, item_type, category_name, stock_status, unit_cost, stock_level, is_active');

      if (fetchError) throw fetchError;

      const itemsList = allItems || [];

      const statsData: ItemMasterStats = {
        total_items: itemsList.length,
        active_items: itemsList.filter(i => i.is_active).length,
        low_stock_items: itemsList.filter(i => i.stock_status === 'low_stock').length,
        out_of_stock_items: itemsList.filter(i => i.stock_status === 'out_of_stock').length,
        total_value: itemsList.reduce((sum, i) => sum + (i.unit_cost * i.stock_level), 0),
        items_by_category: {},
        items_by_type: {
          raw_material: 0,
          finished_good: 0,
          semi_finished: 0,
          consumable: 0,
          spare_part: 0,
          packaging: 0,
        },
      };

      // Count by category
      itemsList.forEach(item => {
        const categoryName = item.category_name || 'Uncategorized';
        statsData.items_by_category[categoryName] = (statsData.items_by_category[categoryName] || 0) + 1;
        
        if (item.item_type && statsData.items_by_type[item.item_type as ItemType] !== undefined) {
          statsData.items_by_type[item.item_type as ItemType]++;
        }
      });

      setStats(statsData);
      return statsData;

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch statistics';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  /**
   * Import items from array
   */
  const importItems = useCallback(async (
    importData: ItemImportData[]
  ): Promise<{ success: number; failed: number; errors: string[] }> => {
    const result = { success: 0, failed: 0, errors: [] as string[] };

    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Get category mapping
      const { data: categories } = await supabase
        .from('item_categories')
        .select('id, code');
      
      const categoryMap = new Map(categories?.map(c => [c.code, c.id]) || []);

      for (const item of importData) {
        try {
          // Validate required fields
          if (!item.code || !item.name || !item.unit) {
            throw new Error(`Missing required fields for item: ${item.code || 'unknown'}`);
          }

          // Map category code to ID
          const categoryId = item.category_code ? categoryMap.get(item.category_code) : null;

          const insertData = {
            code: item.code,
            name: item.name,
            description: item.description || null,
            category_id: categoryId,
            category: item.category_code || 'Uncategorized',
            item_type: (item.item_type as ItemType) || 'raw_material',
            make_or_buy: item.make_or_buy || 'buy',
            unit: item.unit,
            stock_level: item.stock_level ?? 0,
            min_stock_level: item.min_stock_level ?? 0,
            reorder_level: item.reorder_level ?? 0,
            max_stock_level: item.max_stock_level || null,
            lead_time_days: item.lead_time_days ?? 7,
            unit_cost: item.unit_cost ?? 0,
            barcode: item.barcode || null,
            status: 'active',
            is_active: true,
            created_by: user?.id,
          };

          const { error: insertError } = await supabase
            .from('inventory_items')
            .insert(insertData);

          if (insertError) throw insertError;
          result.success++;

        } catch (err) {
          result.failed++;
          result.errors.push(
            `${item.code || 'unknown'}: ${err instanceof Error ? err.message : 'Unknown error'}`
          );
        }
      }

      toast({
        title: 'Import Complete',
        description: `Successfully imported ${result.success} items. ${result.failed} failed.`,
        variant: result.failed > 0 ? 'destructive' : 'default',
      });

      // Refresh items list
      await fetchItems();

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }

    return result;
  }, [fetchItems, toast]);

  /**
   * Export items to array
   */
  const exportItems = useCallback(async (filters?: ItemFilters): Promise<ItemExportData[]> => {
    try {
      let query = supabase
        .from('item_master_view')
        .select('*');

      // Apply same filters as fetchItems
      if (filters?.search) {
        query = query.or(`code.ilike.%${filters.search}%,name.ilike.%${filters.search}%`);
      }
      if (filters?.category_id) {
        query = query.eq('category_id', filters.category_id);
      }
      if (filters?.item_type) {
        query = query.eq('item_type', filters.item_type);
      }
      if (filters?.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }

      const { data, error: fetchError } = await query.order('code', { ascending: true });

      if (fetchError) throw fetchError;
      return data || [];

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      return [];
    }
  }, [toast]);

  return {
    items,
    loading,
    error,
    stats,
    fetchItems,
    getItem,
    createItem,
    updateItem,
    deleteItem,
    generateItemCode,
    fetchStats,
    importItems,
    exportItems,
  };
}

export default useItemMaster;
